import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { IconButton, FontIcon } from 'react-toolbox';
import ReactTooltip from 'react-tooltip';
import { Link } from 'react-router';
import MediaQuery from 'react-responsive';
import Moment from 'moment';
import { urls } from 'helpers/url';
import { navigate } from 'actionCreators';
import { can } from '../../../../../services';
import CalendarAppointmentType from 'types/appointment';
import { RoleType } from 'types/user';
import PhoneLink from 'components/PhoneLink';
import MapLink from 'components/MapLink';
import AppointmentNote from './appointmentNote';
import {
  determineUseActualMOT,
  determineComplete,
  determineMOTTitle,
  determineMOTColorCode,
  determineMOT,
} from '../../../../../utils/motUtils';
import { maxDistanceExceeded, existingApptsRelate } from '../../../../../utils/schedulingUtils';
import { CUT_POINT, DISPLAY_DATE_TIME, RAILS_DATE_TIME } from 'config/constants';
import S from 'appointment.scss';
import cx from 'classnames';

class CalendarAppointment extends Component {
  componentDidUpdate = (prevProps) => {
    const { applySsFeatures, selectedCell, setDelayedField,
            appointment: { id, mapMatrix = { duration: 0 } } } = this.props;
    const { duration } = mapMatrix;
    const prevDuration = (((prevProps.appointment || {}).mapMatrix || {}).duration || 0);
    if (applySsFeatures && selectedCell && id > 0 &&
        duration > 0 && duration !== (prevDuration)) setDelayedField('timeBetween', duration);
  }

  getAppointmentLink = phase => (phase.type === 'Assessment'
    ? urls.phaseHistory(phase) : urls.phaseLaborProductAccounting(phase));

  /* This is the same function as in motUtils.js - determineComplete
  but for calendar appointments we need to modify the logic slightly
  for Assessments. we want to show the complete status if the phase is complete
  Since this isn't MOT related, and a function that is used only in this component
  it made sense to leave this where it is used. */
  determineAptCompletedRibbon = (phaseStatus, phaseTasks) => {
    if (phaseStatus === 'canceled' || phaseStatus === 'completed') return true;

    let complete = false;
    if (Array.isArray(phaseTasks) && phaseTasks.length) {
      phaseTasks.forEach((phaseTask) => {
        if (
            ['install_complete', 'workorder_complete'].includes(phaseTask.slug)
            && phaseTask.status === 'complete'
          ) {
          complete = true;
        }
      });
    }
    return complete;
  }

  altVendorColorCode = (phaseSource, apptKind) => {
    if (phaseSource === 'FD') {
      if (['installation', 'bust_day'].includes(apptKind)) return 'fd_install';
      if (apptKind === 'installation_hold') return 'fd_install_hold';
      if (['repair', 'repair_inspection'].includes(apptKind)) return 'fd_repair';
      if (['crew_inspection', 'jobsite_inspection', 'workorder_hold'].includes(apptKind)) return 'fd_workorder_hold';
    }
    return null;
  }

  render = () => {
    const { key, date, highlight, selectable, onReschedule, pendingId,
      user, useHighContrast, applySsFeatures, appointment: appt, relatedPhases, appointment: {
        user_id,
        arrival_start_time: arrivalStart,
        arrival_end_time: arrivalEnd,
        start_date: startDate,
        end_date: endDate,
        projected_completion: projectedCompletion,
        mapMatrix = { duration: 0, duration_display: '0' },
        note,
        kind,
        phase,
        phase: { workroom, flags, product_type, type, phase_tasks, status, appointments,
          surface_type, sqft, customer_name, city, store_number, store, phone_numbers,
          street_address, state, zip_code, source: phaseSource = '' },
      }, assignee: { name, crew_specific = {} },
    } = this.props;
    const zone = workroom.timezone.includes('Central') ? 'CST' : 'EST';
    let currentDay;
    let totalDays;
    if (startDate !== endDate) {
      const startMoment = Moment(startDate);
      currentDay = date.diff(startMoment, 'days') + 1;
      totalDays = Moment(endDate).diff(startMoment, 'days') + 1;
    }
    const canAttrs = user.role === 'iqm' ?
      { user_name: user.name, obj_name: name } :
      { user_id: user.id, obj_id: user_id };
    const canReschedule = can(user.role, 'update', 'appointment_form', canAttrs);
    const priority = flags.find(flag => flag.name === 'Priority Customer');
    let colorCoding = this.altVendorColorCode(phaseSource, kind)
      || (kind || '').toLowerCase();
    if (colorCoding === 'installation' || colorCoding === 'bust_day') {
      colorCoding = (product_type || 'stk').toLowerCase();
    } else if (colorCoding === 'installation_hold') {
      colorCoding = `${(product_type || 'stk').toLowerCase()}_hold`;
    } else if (type.toLowerCase() === 'assessment') {
      /**
       * Constant declaration has to be in the IF/THEN block since it will only
       * run if the phase type is of "assessment" and departed_from_appointment
       * is only available for assessment phases.
       */
      const hasDepartedTask = phase_tasks
        .find(task => task.slug === 'departed_from_appointment') || { status: '' };
      const hasArrivedTask = phase_tasks
        .find(task => task.slug === 'arrived_at_appointment') || { status: '' };
      const isAppointmentToday = Moment(endDate).diff(Moment().startOf('day'), 'days') === 0;
      const isAppointmentOnTime = Moment(arrivalEnd, 'HH:mm').isAfter(Moment());
      const isArrivedComplete = hasArrivedTask.status === 'complete';
      const isDepartedComplete = hasDepartedTask.status === 'complete';
      if (isAppointmentToday && !isArrivedComplete && !isAppointmentOnTime) colorCoding = 'assessment_due';
      else if (colorCoding === 'delivery') colorCoding = 'delivery';
      else if (colorCoding === 'delivery_hold') colorCoding = 'delivery_hold';
      else if (colorCoding === 'assessment_hold') colorCoding = 'assessment_hold';
      else colorCoding = isDepartedComplete ? '' : 'assessment';
    }
    const complete = determineComplete(status, phase_tasks);
    const showAptCompletedRibbon = this.determineAptCompletedRibbon(status, phase_tasks);
    const useActualMOT = determineUseActualMOT(type, appointments, complete);
    const motTitle = determineMOTTitle(type, useActualMOT, complete);
    const motColorCode = determineMOTColorCode(
      phase,
      useActualMOT,
      complete,
      colorCoding,
    );
    const mot = determineMOT(phase, useActualMOT);
    const { duration, duration_display } = mapMatrix;
    return (<article className={`${S.appointment} ${S[colorCoding]} ${highlight ? S.highlight : ''}`}>
      <Link
        to={this.getAppointmentLink(phase)}
        target="_blank"
        className={cx({
          [S.link]: colorCoding !== 'assessment_due',
          [S.linkAssessmentDue]: colorCoding === 'assessment_due',
        })}
        onClick={() => selectable}
      >
        <header className={S.title}>
          {showAptCompletedRibbon && <div className={S.ribbon}><span>Completed</span></div>}
          <span className={S.time}>
            <time dateTime={arrivalStart}>
              {Moment(arrivalStart, 'HH:mm').format('h:mma')}
            </time> - <time dateTime={arrivalEnd}>
              {Moment(arrivalEnd, 'HH:mm').format('h:mma')} {zone}
            </time>
          </span>
          {totalDays && <span className={S.sequence}>{currentDay}/{totalDays}</span>}
        </header>
        {applySsFeatures &&
         key > 0 &&
         existingApptsRelate(pendingId, [appt], relatedPhases) &&
         <div className={S.related}>Related To This Phase</div>}
        <div className={S.customer}>{customer_name}</div>
        <div className={S.surfaceInfo}>
          {surface_type && surface_type.name}
          {sqft && ` - ${sqft}sqft`}
        </div>
        <div className={S.city}>{city}, {state}</div>
        {can(user.role, 'view', 'created_at_default') && <div className={S.mot}>
          {motTitle}: <span className={S[motColorCode]}>{mot}</span>
        </div>}
        {projectedCompletion && phaseSource !== 'IFI' && <div className={S.estCompletion}>
          Est. Complete: {
            (
              Moment(projectedCompletion, [DISPLAY_DATE_TIME, RAILS_DATE_TIME], true).isValid()
                ? Moment(projectedCompletion, [DISPLAY_DATE_TIME, RAILS_DATE_TIME], true)
                : Moment(projectedCompletion, Moment.ISO_8601, true)
            ).format(DISPLAY_DATE_TIME)
          }
          {` ${zone}`}
        </div>}
        {applySsFeatures && duration > 0 && <div
          className={maxDistanceExceeded(duration, crew_specific.max_travel_time) > 0
            ? S.ssWarning : S.duration}
        >{duration_display} away</div>}
        <div className={S.store}>#{store_number || (store && store.number)}</div>
      </Link>
      <div
        className={cx(S.actions, {
          [S.actionsButtons]: colorCoding !== 'assessment_due',
          [S.actionsButtonsAssessmentDue]: colorCoding === 'assessment_due',
        })}
      >
        {note && <AppointmentNote note={note} customer={customer_name} />}
        {phone_numbers && phone_numbers[0] && <PhoneLink number={phone_numbers[0].number}>
          <FontIcon value="phone" />
        </PhoneLink>}
        <MapLink address={street_address} city={city} state={state} zipCode={zip_code}>
          <FontIcon value="directions" />
        </MapLink>
        {!selectable && canReschedule && <a href={`/phases/${phase.id}/appointments/${appt.id}/edit/${appt.kind}`}>
          <FontIcon value="mode_edit" />
        </a>}
      </div>
      {!selectable && canReschedule && <MediaQuery minWidth={CUT_POINT}>
        <IconButton
          icon="mode_edit"
          onClick={() => onReschedule(appt)}
          className={cx(S.editButton, {
            [S.editMulti]: totalDays,
            [S.editButtonAssessmentDue]: colorCoding === 'assessment_due',
          })}
        />
      </MediaQuery>}
      {selectable && <Link to={urls.phaseLaborProductAccounting(phase)} target="_blank">
        <IconButton
          icon="launch"
          className={cx(S.editButton, {
            [S.editMulti]: totalDays,
            [S.editButtonAssessmentDue]: colorCoding === 'assessment_due',
          })}
        />
      </Link>}
      {priority && <span className={S.priorityFlag}>
        <FontIcon data-tip data-for={`flag-${priority.id}`} value="priority_high" />
        <ReactTooltip
          className={cx(S.tooltip, { [S.tooltip_high_contrast]: useHighContrast })}
          id={`flag-${priority.id}`}
          place="left"
          effect="solid"
        >{priority.note}</ReactTooltip>
      </span>}
    </article>);
  }
}

CalendarAppointment.propTypes = {
  appointment: CalendarAppointmentType.isRequired,
  date: PropTypes.instanceOf(Moment).isRequired,
  highlight: PropTypes.bool,
  selectable: PropTypes.bool,
  onReschedule: PropTypes.func.isRequired,
  setDelayedField: PropTypes.func,
  user: RoleType,
  navigate: PropTypes.func.isRequired,
  useHighContrast: PropTypes.bool,
  assignee: PropTypes.object,
  applySsFeatures: PropTypes.bool,
  relatedPhases: PropTypes.array,
  pendingId: PropTypes.number,
  selectedCell: PropTypes.bool,
  key: PropTypes.number,
};

CalendarAppointment.defaultProps = {
  onReschedule: () => { },
  setDelayedField: () => { },
  applySsFeatures: false,
  selectedCell: false,
};

export default connect(
  state => ({
    user: state.user,
    useHighContrast: state.settings.useHighContrast,
  }),
  { navigate },
)(CalendarAppointment);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/schedule/components/calendarAppointment.jsx